import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, loading, error: authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the page user was trying to access
  const from = (location.state as any)?.from?.pathname || '/admin';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await login(email, password, rememberMe);
      // Redirect to the page they were trying to access, or admin dashboard
      navigate(from, { replace: true });
    } catch (err) {
      // Error is handled by useAuth hook
      console.error('Login failed:', err);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Brand Panel */}
      <div className="hidden lg:flex lg:flex-1 flex-col justify-between p-12" style={{ background: 'var(--gradient-hero)', backdropFilter: 'blur(20px)' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 'var(--font-size-h1)', color: 'var(--color-accent)', marginBottom: '1rem' }}>
            Crisci Create
          </h1>
          <p style={{ color: 'var(--color-accent-light)', fontSize: 'var(--font-size-body-lg)' }}>
            Pharmacy Research Platform
          </p>
        </div>

        <div>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 'var(--font-size-body)', lineHeight: 'var(--line-height-normal)' }}>
            Trusted by leading healthcare providers across Australia for collecting valuable patient insights and research data.
          </p>
        </div>

        <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 'var(--font-size-caption)' }}>
          © 2026 Crisci Group. All rights reserved.
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col justify-center p-8" style={{ backgroundColor: 'var(--color-surface)' }}>
        {/* Back Button */}
        <div className="w-full max-w-md mx-auto mb-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-base font-semibold transition-colors"
            style={{ color: 'var(--color-primary)' }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>

        <div className="w-full max-w-md mx-auto">
          <div className="glass-card glass-card-lg glass-animate-fade-in" style={{ boxShadow: 'var(--shadow-lg)' }}>
            <div className="text-center mb-8">
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'var(--font-size-h2)', color: 'var(--color-primary)', marginBottom: '0.5rem' }}>
                Welcome Back
              </h2>
              <p className="text-muted">Sign in to your admin account</p>
            </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {authError && (
              <div className="glass-card-sm" style={{ borderLeft: '4px solid var(--status-error)', backgroundColor: 'var(--status-error-light)' }}>
                <div className="flex items-start p-3">
                  <svg
                    className="h-5 w-5 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    style={{ color: 'var(--status-error)' }}
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="ml-3">
                    <p className="text-sm" style={{ color: 'var(--status-error)' }}>{authError}</p>
                  </div>
                </div>
              </div>
            )}

            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              disabled={loading}
              required
              autoComplete="email"
            />

            <div>
              <label className="glass-label">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="glass-input pr-12 w-full"
                  disabled={loading}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="custom-checkbox"
                  disabled={loading}
                />
                <span className="ml-2 text-sm" style={{ color: 'var(--color-primary-ink)' }}>Remember me</span>
              </label>

              <button
                type="button"
                onClick={() => navigate('/password-reset')}
                className="text-sm font-medium"
                style={{ color: 'var(--color-accent)' }}
                disabled={loading}
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              size="lg"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted">
            <p>
              Don't have an account?{' '}
              <span>
                Contact your administrator for access
              </span>
            </p>
          </div>
        </div>

        {/* Mobile footer */}
        <div className="mt-6 text-center text-xs lg:hidden" style={{ color: 'var(--color-muted-light)' }}>
          <p>© 2026 Crisci Group. All rights reserved.</p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Login;
